<template>
    <teleport to="body">
        <CompassModal
            :visible="visible"
            size="large"
            :title="$t.__('email.EmailCustomTemplateModal.title_dialog')"
            @update:visible="handleCloseModal"
        >
            <template #default>
                <div
                    v-if="isNoTemplateOnList && !isLoadingGetTemplates"
                    class="email-custom-template-modal__no-template-on-list"
                >
                    <CompassEmptyState
                        illustrationPath="/ressources/logged/img/illustrations/custom-email-template-no-result.svg"
                        :title="$t.__('email.EmailCustomTemplateModal.title_no_template')"
                        :caption="$t.__('email.EmailCustomTemplateModal.caption_no_template')"
                    >
                        <CompassButton
                            :label="$t.__('email.EmailCustomTemplateModal.btn_label_no_template')"
                            data-bot="email-custom-template-modal__no-template-button"
                            @click="handleNewTemplate"
                        />
                    </CompassEmptyState>
                </div>
                <div v-else class="email-custom-template-modal__container">
                    <div class="email-custom-template-modal__list">
                        <CompassInput
                            v-model="searchQuery"
                            type="search"
                            :placeholder="$t.__('common.actions.search')"
                            prefixIcon="search"
                            :disabled="isDisabledInput"
                            class="email-custom-template-modal__search"
                            data-bot="email-custom-template-modal__search"
                        >
                            <template #suffix>
                                <CompassIconButton
                                    v-show="hasSearchQuery"
                                    icon="close"
                                    size="small"
                                    :disabled="isDisabledInput"
                                    data-bot="email-custom-template-modal__reset"
                                    @click="resetInput"
                                />
                            </template>
                        </CompassInput>
                        <div class="email-custom-template-modal__list-container">
                            <CompassEmptyState
                                v-if="isNoFilterResult"
                                illustrationPath="/ressources/logged/img/illustrations/no-results.svg"
                                :title="$t.__('email.EmailCustomTemplateModal.title_no_result')"
                                :caption="$t.__('email.EmailCustomTemplateModal.caption_no_result')"
                            />
                            <template v-else-if="isLoadingGetTemplates">
                                <CompassSkeleton
                                    v-for="(skeletonClass, index) in skeletonListTemplateClasses"
                                    :key="index"
                                    size="large"
                                    :class="[
                                        'email-custom-template-modal__skeleton',
                                        skeletonClass,
                                    ]"
                                />
                            </template>

                            <li
                                v-for="item in filteredItems"
                                v-else
                                :key="item.id"
                                :class="listItemClasses(item)"
                                data-bot="email-custom-template-modal__item"
                                @click="$emit('selectTemplate', item.id)"
                            >
                                {{ item.name }}
                                <CompassIcon
                                    v-if="item.hasAi"
                                    icon="ai-sparkles-simple"
                                    size="small"
                                    class="email-custom-template-modal__ai-icon"
                                />
                            </li>
                        </div>
                        <div
                            class="email-custom-template-modal__btn-new-template"
                            data-bot="email-custom-template-modal__btn-new-template"
                            @click="handleNewTemplate()"
                        >
                            <CompassIcon icon="plus" type="link" />
                            <span class="typographies-paragraph-medium-semibold">
                                {{ $t.__('email.EmailCustomTemplateModal.create_new_template') }}
                            </span>
                        </div>
                    </div>
                    <div class="email-custom-template-modal__details">
                        <div
                            v-if="isLoadingSelectTemplate"
                            class="email-custom-template-modal__details--skeleton-container"
                        >
                            <CompassSkeleton size="large" :class="skeletonTemplateTitleClasses" />
                            <CompassSkeleton
                                v-for="(skeletonClass, index) in skeletonTemplateClasses"
                                :key="index"
                                :class="['email-custom-template-modal__skeleton', skeletonClass]"
                            />
                        </div>
                        <div
                            v-else-if="selectedTemplate"
                            class="email-custom-template-modal__details-content rich-text-style"
                        >
                            <h3
                                class="typographies-heading-h3"
                                v-html="formattedContent(selectedTemplate.subject)"
                            />
                            <div
                                class="typographies-paragraph-medium-regular email-custom-template-modal__details-content__text"
                                :class="editorClass"
                                v-html="formattedContent(selectedTemplate.message)"
                            ></div>

                            <div
                                v-if="selectedTemplate.attachments.length > 0"
                                class="email-custom-template-modal__attachments"
                            >
                                <EmailAttachmentLink
                                    v-for="attachment in selectedTemplate.attachments"
                                    :key="attachment.id"
                                    :file="attachment"
                                    size="small"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template v-if="!isNoTemplateOnList" #footer>
                <div class="email-custom-template-modal__footer">
                    <div
                        v-if="showRemainingQuota"
                        class="email-custom-template-modal__remaining-quota"
                    >
                        <CompassFeatureBadge
                            v-if="quotaAILimitReached"
                            variant="upsell"
                            size="extra-small"
                        />
                        <img
                            v-else
                            src="/ressources/logged/img/icon/svg/ai-icon-write.svg"
                            alt="ai icon"
                            class="ai-icon"
                        />
                        <span class="typographies-paragraph-x-small-regular">
                            {{ remainingQuotaLabel }}
                        </span>
                    </div>
                    <CompassButton
                        :label="$t.__('common.actions.cancel')"
                        type="secondary"
                        :disabled="isLoadingAddTemplateOnContentEmail"
                        @click="handleCloseModal"
                    />

                    <CompassButton
                        :label="getLabelPrimaryAction"
                        :loading="isLoadingAddTemplateOnContentEmail"
                        @click="$emit('addTemplateOnContentEmail', selectedTemplate.id)"
                    />
                </div>
            </template>
        </CompassModal>
    </teleport>
</template>

<script>
import { createPopper } from '@popperjs/core';
import { createNamespacedHelpers } from 'vuex';
import { goToUrl } from 'vueDir/helpers.js';
import Feature from 'Services/Feature';
import SellsyAIRichTextMixin from 'mixins/SellsyAIRichTextMixin';
import EmailAttachmentLink from 'vcomponents/email/EmailAttachmentLink.vue';
import { getEmailTemplateTags } from 'api/emailEngine/emailEngineService';
import { editorClass } from 'Helpers/richTextHelper.js';
const {
    mapState: corpMapState,
    mapGetters: corpMapGetters,
    mapActions: corpMapActions,
} = createNamespacedHelpers('corp');

export default {
    components: {
        EmailAttachmentLink,
    },
    mixins: [SellsyAIRichTextMixin],
    inject: ['parentHash'],
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        initCurrentRoute: {
            type: Object,
            required: true,
        },
        templates: {
            type: Array,
            default: () => [],
        },
        selectedTemplate: {
            type: Object,
            default: () => ({}),
        },
        isLoadingGetTemplates: {
            type: Boolean,
            default: false,
        },
        isLoadingSelectTemplate: {
            type: Boolean,
            default: false,
        },
        isLoadingAddTemplateOnContentEmail: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['close', 'selectTemplate', 'addTemplateOnContentEmail'],
    data() {
        return {
            searchQuery: '',
            filteredItems: [],
            skeletonListTemplateClasses: [
                'email-custom-template-modal__skeleton--w-275',
                'email-custom-template-modal__skeleton--w-270',
                '',
                'email-custom-template-modal__skeleton--w-225',
                'email-custom-template-modal__skeleton--w-250',
                '',
                'email-custom-template-modal__skeleton--w-225',
                'email-custom-template-modal__skeleton--w-270',
                '',
                'email-custom-template-modal__skeleton--w-270',
            ],
            skeletonTemplateTitleClasses: [
                'email-custom-template-modal__skeleton',
                'email-custom-template-modal__skeleton--title',
                'email-custom-template-modal__skeleton--w-250',
            ],
            skeletonTemplateClasses: [
                'email-custom-template-modal__skeleton--w-225',
                'email-custom-template-modal__skeleton--w-500',
                'email-custom-template-modal__skeleton--w-450',
                'email-custom-template-modal__skeleton--w-525',
                'email-custom-template-modal__skeleton--w-500',
                'email-custom-template-modal__skeleton--w-525',
                'email-custom-template-modal__skeleton--w-525',
                'email-custom-template-modal__skeleton--w-500',
            ],
            tags: [],
            popperInstance: null,
        };
    },
    computed: {
        ...corpMapState(['plan', 'pricingVersion']),
        ...corpMapGetters(['getQuotaAI', 'quotaAILimitReached']),
        isSellsyAIEnabled() {
            return Feature.isEnabled('sellsy-ai');
        },
        isNoTemplateOnList() {
            return this.templates.length === 0;
        },
        isNoFilterResult() {
            return this.filteredItems?.length === 0;
        },
        isDisabledInput() {
            return this.isLoadingGetTemplates;
        },
        hasSearchQuery() {
            return this.searchQuery.length > 0;
        },
        getLabelPrimaryAction() {
            return this.isNoTemplateOnList
                ? this.$t.__('email.EmailCustomTemplateModal.primary_btn_create_new_template')
                : this.$t.__('common.actions.insert');
        },
        showRemainingQuota() {
            if (!this.isSellsyAIEnabled) {
                return false;
            } else if (this.plan && this.pricingVersion === 5) {
                return !(this.plan.includes('ELITE') || this.plan.includes('EVOLUTION'));
            }

            return true;
        },
        remainingQuota() {
            if (!this.getQuotaAI) {
                return null;
            }
            return this.getQuotaAI?.limit - this.getQuotaAI?.used;
        },
        remainingQuotaLabel() {
            if (this.quotaAILimitReached) {
                return this.$t.__('ai.SellsyAIMenu.label_limit_quota');
            }

            return this.$t.n__(
                'ai.SellsyAIMenu.label_remaining_quota',
                null,
                this.remainingQuota,
                this.remainingQuota,
            );
        },
        editorClass,
    },
    watch: {
        visible(newValue) {
            if (!newValue) {
                this.hideAllPopovers();
            }
        },
        searchQuery() {
            this.filterItems();
        },
        templates(newValues) {
            this.filteredItems = [...newValues];
        },
    },
    async created() {
        await this.getTags();
        if (!this.isNoTemplateOnList && this.isSellsyAIEnabled) {
            await this.fetchQuotaAI();
        }
    },
    mounted() {
        this.attachEventListenersPopover();
    },
    beforeUnmount() {
        this.detachEventListenersPopover();
        this.resetModal();
    },
    methods: {
        ...corpMapActions(['fetchQuotaAI']),
        formattedContent(text) {
            if (!text) {
                return '';
            }
            return this.replaceTagAIWithSpan(this.replaceTagsWithSpan(text));
        },
        replaceTagAIWithSpan(message) {
            for (const relatedAI of this.selectedTemplate.related_ai) {
                const aiTagRegex = new RegExp(`%ai{id=["']${relatedAI.id}["'].*?}ai%`, 'g');

                const svgAIIcon = `
                    <svg viewBox="0 0 24 24" class="compass-icon custom-tags-and-fields__ai-icon compass-icon--medium">
                        <path d="M11.998 1C11.998 2.4592 11.8141 7.79629 10.0431 9.55133C8.19208 11.4023 2.22333 11.5502 1 11.5502C2.22333 11.5502 8.19607 11.6902 10.0431 13.5492C11.8141 15.2922 11.986 20.6173 11.998 22.0885C11.998 20.6253 12.1819 15.2922 13.9569 13.5332C15.8039 11.6862 21.7727 11.5542 23 11.5343C21.7727 11.5343 15.8039 11.3983 13.9569 9.53534C12.1819 7.7803 12.014 2.4592 11.998 1Z" fill="currentColor"></path>
                    </svg>
                `;

                const newSpan = `
                    <span
                        type="ai"
                        label="${relatedAI.title}"
                        prompt="${relatedAI.prompt}"
                        class="custom-tags-and-fields custom-tags-and-fields__ai custom-tags-and-fields__ai-preview"
                        hash="${relatedAI.id}"
                    >
                        ${svgAIIcon}
                        <span class="typographies-paragraph-small-semibold">
                            ${relatedAI.title}
                        </span>
                        <div
                            hash="${relatedAI.id}"
                            class="rich-text-style custom-tags-and-fields__popover custom-tags-and-fields__popover-preview"
                            style="display: none;"
                        >
                            <div class="custom-tags-and-fields__popover-header">
                                <span class="typographies-paragraph-small-bold">
                                    ${this.$t.__('richtext.CustomTagsAndFields.popover_title_ai')}
                                </span>
                                ${svgAIIcon}
                            </div>
                            <div class="custom-tags-and-fields__popover-content rich-text-style">
                                ${this.replaceTagsWithSpan(relatedAI.prompt)}
                            </div>
                        </div>
                    </span>
                `;
                message = message.replace(aiTagRegex, newSpan);
                this.attachEventListenersPopover();
            }

            return message;
        },
        replaceTagsWithSpan(message) {
            for (const tag of this.tags) {
                const regex = new RegExp(tag.key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
                message = message.replace(
                    regex,
                    `<span class="custom-tags-and-fields custom-tags-and-fields__default typographies-paragraph-small-semibold" tag="${tag.key}">${tag.name}</span>`,
                );
            }
            return message;
        },
        attachEventListenersPopover() {
            setTimeout(() => {
                const spans = document.querySelectorAll('.custom-tags-and-fields__ai-preview');
                for (const span of spans) {
                    span.addEventListener('mouseenter', this.showPopover);
                    span.addEventListener('mouseleave', this.hidePopover);
                }
            }, 0);
        },
        detachEventListenersPopover() {
            setTimeout(() => {
                const spans = document.querySelectorAll('.custom-tags-and-fields__ai-preview');
                for (const span of spans) {
                    span.removeEventListener('mouseenter', this.showPopover);
                    span.removeEventListener('mouseleave', this.hidePopover);
                }
            }, 0);
        },
        showPopover(event) {
            const target = event.currentTarget;
            const spanHash = target.getAttribute('hash');
            const popover = document.querySelector(
                `.custom-tags-and-fields__popover[hash="${spanHash}"]`,
            );
            if (
                popover &&
                popover.classList.contains('custom-tags-and-fields__popover') &&
                target.classList.contains('custom-tags-and-fields__ai-preview')
            ) {
                document.body.appendChild(popover);
                this.popperInstance = createPopper(target, popover, {
                    placement: 'bottom-start',
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 4],
                            },
                        },
                    ],
                });
                popover.style.display = 'block';
            }
        },
        hidePopover(event) {
            const target = event.currentTarget;
            const spanHash = target.getAttribute('hash');
            const popover = document.querySelector(
                `.custom-tags-and-fields__popover[hash="${spanHash}"]`,
            );
            if (popover) {
                popover.style.display = 'none';
                this.resetPopperInstance();
            }
        },
        hideAllPopovers() {
            const popovers = document.querySelectorAll('.custom-tags-and-fields__popover');
            for (const popover of popovers) {
                popover.style.display = 'none';
            }

            this.resetPopperInstance();
        },
        resetPopperInstance() {
            if (this.popperInstance) {
                this.popperInstance.destroy();
                this.popperInstance = null;
            }
        },
        listItemClasses(item) {
            return [
                'email-custom-template-modal__list-item',
                'typographies-paragraph-medium-regular',
                this.isSelected(item) ? 'email-custom-template-modal__list-item--selected' : '',
            ];
        },
        filterItems() {
            this.filteredItems = this.templates.filter((template) =>
                template.name.toLowerCase().includes(this.searchQuery.toLowerCase()),
            );
        },
        isSelected(item) {
            return this.selectedTemplate ? this.selectedTemplate.id === item.id : false;
        },
        resetModal() {
            this.filteredItems = [];
            this.searchQuery = '';
        },
        resetInput() {
            this.searchQuery = '';
            this.filterItems();
        },
        minimizeCurrentPanel() {
            const headerActionsParent = document.querySelector('.compass-panel__header-actions');
            if (headerActionsParent) {
                const minimizedButton = headerActionsParent.querySelector('button');
                if (minimizedButton) {
                    minimizedButton.click();
                }
            }
        },
        redirectToSettingsEmailCustom() {
            const currentUrl = this.initCurrentRoute.fullPath.toLowerCase();
            const targetUrl = '/emails/templates';
            if (currentUrl !== targetUrl.toLowerCase()) {
                goToUrl(targetUrl);
            }
        },
        handleNewTemplate() {
            this.handleCloseModal();
            this.minimizeCurrentPanel();
            this.redirectToSettingsEmailCustom();
        },
        handleCloseModal() {
            this.$emit('close');
        },
        getTags() {
            getEmailTemplateTags()
                .then((response) => {
                    this.tags = response.data;
                })
                .catch(() => {
                    this.tags = [];
                    this.$pushToast({
                        type: 'error',
                        icon: true,
                        text: this.$t.__('email.SettingsEmailsTemplates.error_text_get_tags'),
                        close: true,
                    });
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.email-custom-template-modal {
    &__no-template-on-list {
        margin: 0 0 var(--spacing-5);
    }
    &__container {
        display: flex;
    }
    &__list {
        display: flex;
        flex-direction: column;
        margin: var(--spacing-1);
        &-container {
            width: 312px;
            height: 404px;
            margin: var(--spacing-1) 0 0;
            overflow-y: auto;
        }
        &-item {
            display: flex;
            gap: var(--spacing-1);
            align-items: center;
            padding: 10px var(--spacing-1);
            cursor: pointer;
            border-radius: var(--spacing-0);
            color: var(--colors-common-text-standard-default, #111111);
            &:hover {
                background-color: var(--colors-component-background-actionable-hover);
            }

            &--selected {
                background-color: var(--colors-component-background-actionable-hover);
            }
        }
    }
    &__no-filter-result {
        text-align: center;
        & + .compass-empty-state {
            gap: 0;
        }
    }
    &__search {
        margin: 0 0 var(--spacing-0);
    }
    &__btn-new-template {
        display: flex;
        align-content: center;
        gap: var(--spacing-1);
        margin: var(--spacing-1);
        padding: var(--spacing-1);
        cursor: pointer;
        border-radius: var(--spacing-0);
        color: var(--colors-common-text-primary-default);
        &:hover {
            background-color: var(--colors-component-background-actionable-hover);
        }
    }
    &__details {
        display: flex;
        flex: 1;
        min-width: 0;

        &--skeleton-container {
            display: flex;
            flex-direction: column;
        }

        &-content {
            width: 100%;
            padding: var(--spacing-1) var(--spacing-3);

            &__text {
                max-height: 452px;
                overflow: auto;

                &::v-deep img {
                    max-width: 100%;
                }
            }
        }
        &::before {
            content: '';
            min-width: 1px;
            height: auto;
            background: var(--colors-common-border-lighter);
            margin: var(--spacing-1) var(--spacing-2);
        }

        h3 {
            margin: 0 0 var(--spacing-4);
        }
    }
    &__skeleton {
        padding: 10px var(--spacing-1);
        &--title {
            margin: 0 0 var(--spacing-4);
        }
        &--w-525 {
            width: 525px;
        }
        &--w-500 {
            width: 500px;
        }
        &--w-450 {
            width: 450px;
        }
        &--w-275 {
            width: 275px;
        }
        &--w-270 {
            width: 270px;
        }
        &--w-250 {
            width: 250px;
        }
        &--w-225 {
            width: 225px;
        }
    }

    &__attachments {
        display: flex;
        flex-wrap: wrap;
        gap: var(--spacing-0);
        margin: var(--spacing-3) 0 0;
    }

    &__ai-icon::v-deep.compass-icon {
        background: var(--colors-sellsy-ai-05);
        border-radius: 50%;
        path {
            fill: var(--colors-sellsy-ai-50);
        }
    }

    &__footer {
        align-items: center;
        display: flex;
        gap: var(--spacing-3);
        justify-content: flex-end;
        padding: var(--spacing-4) var(--spacing-5) var(--spacing-5) 0;
    }

    &__remaining-quota {
        align-items: center;
        color: var(--colors-common-text-standard-default);
        display: flex;
        gap: var(--spacing-0);

        .ai-icon {
            flex-shrink: 0;
            height: 14px;
            width: 14px;
        }

        &::v-deep {
            .compass-feature-badge {
                flex-shrink: 0;
            }
        }
    }
}
</style>
